import { BeMappedNode } from "src/constants/NodeDataTypes";
import {
  CodeNodeSuggestion,
  NodeSuggestion,
} from "src/copilot/CodeNodeSuggestion";
import { logger } from "src/utils/logger";
import { errorMessage } from "src/utils/stringUtils";

const NODE_SUGGESTION_OPEN = "<node_suggestion>";
const NODE_SUGGESTION_CLOSE = "</node_suggestion>";

const TextContent: React.FC<{ content: string }> = ({ content }) => (
  <p className="whitespace-pre-line text-gray-800 font-inter-normal-13px">
    {content}
  </p>
);

type RenderSuggestionFnArgs = {
  suggestion: string;
  originalNode: BeMappedNode;
  key: number;
  onApply: (suggestion: NodeSuggestion) => void;
};

const renderSuggestion = ({
  suggestion,
  originalNode,
  key,
  onApply,
}: RenderSuggestionFnArgs) => {
  try {
    const suggestionObj = JSON.parse(suggestion);

    if (
      "meta" in suggestionObj &&
      "name" in suggestionObj &&
      "type" in suggestionObj
    ) {
      return (
        <CodeNodeSuggestion
          key={key}
          nodeSuggestion={suggestionObj as NodeSuggestion}
          originalNode={originalNode}
          onApply={onApply}
        />
      );
    } else {
      throw new Error(suggestionObj);
    }
  } catch (e) {
    logger.error("Invalid node suggestion format", e);
    return (
      <TextContent
        key={key}
        content={`Unable to load node suggestion: ${errorMessage(e)}`}
      />
    );
  }
};

type ReplaceNodeSuggestionsFnArgs = {
  content: string;
  originalNode: BeMappedNode;
  onApply: (suggestion: NodeSuggestion) => void;
};

export const replaceNodeSuggestions = ({
  content,
  originalNode,
  onApply,
}: ReplaceNodeSuggestionsFnArgs): React.ReactNode => {
  if (!content.includes(NODE_SUGGESTION_OPEN)) {
    return <TextContent content={content} />;
  }

  const parts: React.ReactNode[] = [];
  let keyCount = 0;
  let contentToSearch = content;

  while (true) {
    const [tagOpenIndex, tagCloseIndex] = [
      contentToSearch.indexOf(NODE_SUGGESTION_OPEN),
      contentToSearch.indexOf(NODE_SUGGESTION_CLOSE),
    ];
    const [first, suggestion, rest] = [
      contentToSearch.slice(0, tagOpenIndex),
      contentToSearch.slice(
        tagOpenIndex + NODE_SUGGESTION_OPEN.length,
        tagCloseIndex,
      ),
      contentToSearch.slice(tagCloseIndex + NODE_SUGGESTION_CLOSE.length),
    ];

    parts.push(<TextContent key={keyCount} content={first.trim()} />);
    parts.push(
      renderSuggestion({
        suggestion,
        originalNode,
        key: keyCount + 1,
        onApply,
      }),
    );

    if (rest.includes(NODE_SUGGESTION_OPEN)) {
      keyCount += 2;
      contentToSearch = rest;
    } else {
      parts.push(<TextContent key={keyCount + 2} content={rest.trim()} />);
      break;
    }
  }

  return parts;
};
